import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackObservabilityEvent } from '@/observability/monitoring';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    trackObservabilityEvent('react:render-error', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="site-noise relative flex min-h-screen items-center justify-center bg-background px-4 py-8 text-foreground">
          <section className="brand-frame w-full max-w-xl rounded-xl border border-border bg-card/85 p-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Error</p>
            <h1 className="mt-3 font-display text-4xl text-foreground">Something went wrong</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Reload the page or return to the FIRE Calculator.
            </p>
            <div className="mt-6 flex items-center justify-center">
              <a
                href="/"
                className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-soft transition hover:brightness-110"
              >
                Return to FIRE Calculator
              </a>
            </div>
          </section>
        </div>
      );
    }

    return this.props.children;
  }
}
